import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Scale, AlertCircle, Loader2, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import './ExceptionalBalance.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExceptionalBalance = () => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      toast.error('Please upload only Excel files (.xlsx, .xls)');
      return;
    }
    setFile(selectedFile);
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Please upload a file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/trial-balance/exceptional-balance`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const responseData = response.data.data;
      const nestedData = buildHierarchy(responseData);
      setData(nestedData);

      if (responseData.length > 0) {
        setColumns(Object.keys(responseData[0]));
      }
      toast.success('Analysis completed successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (row) => {
    row.expanded = !row.expanded;
    setData([...data]); // trigger re-render
  };

  const renderRows = (rows, level = 0) =>
    rows.map((row, index) => (
      <React.Fragment key={row.id || index}>
        <tr className={row.level === 0 ? 'table-row-root' : ''}>
          {columns.map((col, idx) => (
            <td
              key={col}
              className={col === 'particulars' ? 'sticky-cell particulars-cell' : ''}
              style={col === 'particulars' ? { paddingLeft: `${level * 20 + 12}px` } : {}}
            >
              {col === 'particulars' && row.subRows && row.subRows.length > 0 && (
                <button
                  className={`expand-btn ${row.expanded ? 'expanded' : ''}`}
                  onClick={() => toggleRow(row)}
                >
                  ▶
                </button>
              )}
              {formatCellValue(col, row[col])}
            </td>
          ))}
        </tr>

        {row.subRows && row.subRows.length > 0 && row.expanded && renderRows(row.subRows, level + 1)}
      </React.Fragment>
    ));

  const formatCellValue = (columnName, value) => {
    if (value === null || value === undefined) return '-';
    
    // Format balance type columns
    if (columnName === 'opening_balance_type' || columnName === 'closing_balance_type') {
      if (value === 0) return 'Credit';
      if (value === 1) return 'Debit';
      return '-';
    }
    
    return value;
  };

  return (
    <div className="space-y-6" data-testid="exceptional-balance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Scale className="h-8 w-8 mr-3 text-purple-600" />
            Exceptional Balance Checker
          </h1>
          <p className="text-gray-600 mt-2">
            Validate trial balance entries against golden accounting rules
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="font-semibold text-blue-900">About Exceptional Balance</h3>
              <p className="text-sm text-blue-800 mt-1">
                This tool validates your trial balance against standard accounting golden rules. It checks if Opening and Closing balances follow the correct Debit/Credit convention based on account types.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Trial Balance</CardTitle>
          <CardDescription>
            Upload your trial balance file for validation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file">Trial Balance File (.xlsx)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                  data-testid="trial-balance-file-input"
                />
                {file && <CheckCircle className="h-5 w-5 text-green-600" />}
              </div>
              {file && <p className="text-sm text-gray-600">{file.name}</p>}
            </div>

            <Button
              onClick={handleAnalyze}
              disabled={!file || loading}
              className="w-full md:w-auto"
              data-testid="analyze-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Validate Balance
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Exceptional Balances Table</CardTitle>
            <CardDescription>
              Hierarchical view of balance validation results (Click ▶ to expand)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col}>{col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>{renderRows(data)}</tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Helper: convert flat array to nested hierarchy
function buildHierarchy(flatData) {
  const root = [];
  const stack = [];
  flatData.forEach((row) => {
    const newRow = { ...row, subRows: [], expanded: false };
    while (stack.length && stack[stack.length - 1].level >= newRow.level) stack.pop();
    if (stack.length === 0) root.push(newRow);
    else stack[stack.length - 1].subRows.push(newRow);
    stack.push(newRow);
  });
  return root;
}

export default ExceptionalBalance;
